import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { MetaBox } from '@/components/admin/MetaBox';
import { ErrorState, ForbiddenState } from '@/components/admin/states';
import { EVENT_STATUS_LABELS, fetchEvents, type EventItem } from '@/features/events/api';
import {
  buildEventTypeTabs,
  EVENT_SCHEDULE_OPTIONS,
  eventMatchesSchedule,
  EventTypeSection,
  FilterTabRow,
  type EventScheduleFilter,
} from '@/features/events/eventBrowse';
import { fetchEventTypes } from '@/features/masters/api';
import { fetchStaff } from '@/features/staff/api';
import { STAFF_ROLE_META, type StaffRole } from '@/features/staff/StaffPage';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

const PER_PAGE = 100;

/**
 * 担当者ごとの担当イベント一覧（事務局 / D-301・D-303）。
 * 会員の参加イベントと同じ「時期＋種別」の絞り込み。
 */
export function StaffMeetingsPage({ role }: { role: StaffRole }): ReactElement {
  const { userId = '' } = useParams();
  const navigate = useNavigate();
  const meta = STAFF_ROLE_META[role];
  const [schedule, setSchedule] = useState<EventScheduleFilter>('all');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [draftSearch, setDraftSearch] = useState('');

  const staffQuery = useQuery({
    queryKey: ['staff', role],
    queryFn: () => fetchStaff({ role }),
  });
  const staff = useMemo(
    () => (staffQuery.data ?? []).find((s) => s.user_id === userId) ?? null,
    [staffQuery.data, userId],
  );

  // 1回取得して時期はクライアントで分ける（会員側と同じ数え方・矛盾しない）
  const eventsQuery = useQuery({
    queryKey: ['events', 'staff-assigned', userId],
    queryFn: () =>
      fetchEvents({
        page: 1,
        per_page: PER_PAGE,
        staff_user_id: userId,
      }),
    enabled: Boolean(userId),
  });
  const typesQuery = useQuery({
    queryKey: ['event-types'],
    queryFn: fetchEventTypes,
    staleTime: 5 * 60_000,
  });

  const allRows = eventsQuery.data?.items ?? [];

  const scheduleCounts = useMemo(() => {
    const now = Date.now();
    let upcoming = 0;
    let past = 0;
    for (const row of allRows) {
      if (eventMatchesSchedule(row.starts_at, 'upcoming', now)) upcoming += 1;
      else past += 1;
    }
    return { all: allRows.length, upcoming, past };
  }, [allRows]);

  const filtered = useMemo(() => {
    const now = Date.now();
    const bySchedule = allRows.filter((row) =>
      eventMatchesSchedule(row.starts_at, schedule, now),
    );
    const q = search.trim().toLowerCase();
    if (!q) return bySchedule;
    return bySchedule.filter(
      (row) =>
        row.title.toLowerCase().includes(q) ||
        row.type_name.toLowerCase().includes(q) ||
        (row.member_name ?? '').toLowerCase().includes(q),
    );
  }, [allRows, schedule, search]);

  const typeTabs = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of filtered) {
      counts.set(row.type_name, (counts.get(row.type_name) ?? 0) + 1);
    }
    return buildEventTypeTabs(counts, typesQuery.data);
  }, [filtered, typesQuery.data]);

  const visible = useMemo(() => {
    if (typeFilter === null) return filtered;
    return filtered.filter((row) => row.type_name === typeFilter);
  }, [filtered, typeFilter]);

  const groups = useMemo(() => {
    if (typeFilter !== null) {
      return [{ typeName: typeFilter, rows: visible }];
    }
    const map = new Map<string, EventItem[]>();
    for (const row of visible) {
      const list = map.get(row.type_name) ?? [];
      list.push(row);
      map.set(row.type_name, list);
    }
    return typeTabs
      .filter((t) => (map.get(t.name)?.length ?? 0) > 0)
      .map((t) => ({ typeName: t.name, rows: map.get(t.name)! }));
  }, [visible, typeTabs, typeFilter]);

  useEffect(() => {
    if (typeFilter !== null && !typeTabs.some((t) => t.name === typeFilter)) {
      setTypeFilter(null);
    }
  }, [typeFilter, typeTabs]);

  useEffect(() => {
    setTypeFilter(null);
  }, [schedule]);

  function renderTable(rows: EventItem[]): ReactElement {
    return (
      <div className="overflow-x-auto rounded-sm border border-wpborder">
        <table className="w-full border-collapse text-left text-wptable">
          <thead>
            <tr className="border-b border-wpborder bg-wpcanvas/60">
              <th className="px-5 py-3.5 font-semibold text-wptext-strong">イベント</th>
              <th className="px-5 py-3.5 font-semibold text-wptext-strong">受講生</th>
              <th className="px-5 py-3.5 font-semibold text-wptext-strong">開催日時</th>
              <th className="px-5 py-3.5 font-semibold text-wptext-strong">ステータス</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => navigate(`/events/${row.id}`)}
                className="cursor-pointer border-b border-wpborder last:border-b-0 hover:bg-wpcanvas/60"
              >
                <td className="px-5 py-4 align-middle">
                  <Link
                    to={`/events/${row.id}`}
                    className="text-wpaccent hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row.title}
                  </Link>
                </td>
                <td className="whitespace-nowrap px-5 py-4 align-middle">
                  {row.counterpart_role !== null ? (
                    (row.member_name ?? '—')
                  ) : (
                    <span className="text-wptext-muted">—</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-5 py-4 align-middle">
                  {row.starts_at ? formatDateTime(row.starts_at) : '日程調整中'}
                </td>
                <td className="whitespace-nowrap px-5 py-4 align-middle">
                  {EVENT_STATUS_LABELS[row.status]}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  let body: ReactElement;
  if (staffQuery.isPending || eventsQuery.isPending) {
    body = <p className="text-wptext-muted">読み込み中…</p>;
  } else if (staffQuery.isError || eventsQuery.isError) {
    const err = staffQuery.error ?? eventsQuery.error;
    body =
      err instanceof ApiError && err.status === 403 ? (
        <ForbiddenState />
      ) : (
        <ErrorState
          onRetry={() => {
            void staffQuery.refetch();
            void eventsQuery.refetch();
          }}
        />
      );
  } else if (typeFilter !== null && visible.length === 0) {
    body = (
      <p className="text-wptext-muted">
        {search.trim()
          ? '検索条件に一致するイベントはありません。'
          : `この時期の${typeFilter}はありません。`}
      </p>
    );
  } else if (groups.length === 0) {
    body = (
      <p className="text-wptext-muted">
        {search.trim()
          ? '検索条件に一致するイベントはありません。'
          : schedule === 'upcoming'
            ? 'これからの担当イベントはありません。'
            : schedule === 'past'
              ? '終了した担当イベントはありません。'
              : '担当イベントはありません。'}
      </p>
    );
  } else if (typeFilter !== null) {
    body = renderTable(visible);
  } else {
    body = (
      <div>
        {groups.map((group) => (
          <EventTypeSection key={group.typeName} title={group.typeName} count={group.rows.length}>
            {renderTable(group.rows)}
          </EventTypeSection>
        ))}
      </div>
    );
  }

  const staffName = staff?.display_name ?? staff?.email ?? '担当者';

  return (
    <>
      <PageHeader
        title={`${staffName}の担当イベント`}
        description="時期と種別で絞り込めます。行を押すと詳細が開きます。"
      />

      <MetaBox title="担当イベント">
        <FilterTabRow
          label="時期"
          active={schedule}
          onChange={(value) => setSchedule(value as EventScheduleFilter)}
          options={EVENT_SCHEDULE_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label,
            count: scheduleCounts[opt.value],
          }))}
        />
        <FilterTabRow
          label="種別"
          active={typeFilter ?? ''}
          onChange={(value) => setTypeFilter(value === '' ? null : value)}
          options={[
            { value: '', label: 'すべて', count: filtered.length },
            ...typeTabs.map((tab) => ({
              value: tab.name,
              label: tab.name,
              count: tab.count,
            })),
          ]}
        />

        <form
          className="mb-3 flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setSearch(draftSearch);
          }}
        >
          <label htmlFor={`staff-events-search-${userId}`} className="sr-only">
            イベント名で検索
          </label>
          <input
            id={`staff-events-search-${userId}`}
            type="search"
            value={draftSearch}
            placeholder="イベント名・受講生で検索"
            onChange={(event) => setDraftSearch(event.target.value)}
            className="rounded-sm border border-wpborder px-3 py-2"
          />
          <button type="submit" className="btn-secondary">
            検索
          </button>
        </form>

        {body}
      </MetaBox>

      <button
        type="button"
        onClick={() => navigate(meta.path)}
        className="mt-4 inline-block text-wpaccent hover:underline"
      >
        ← {meta.label}一覧に戻る
      </button>
    </>
  );
}
